import { useEffect, useState } from "react";
import { FileSpreadsheet, Loader2, Check, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { invoiceExportSettings, saveInvoiceExportSettings } from "../lib/api";

/**
 * The mapping that decides whether an export imports cleanly or quietly
 * poisons someone's ledger. Xero matches contacts on the name EXACTLY and
 * creates a duplicate on a near-miss; a blank tax rate imports the line with
 * no tax at all. Both live here rather than being guessed per export.
 */
const CODE_FIELDS = [
  ["tow", "Towing income"],
  ["impound", "Impound fees"],
  ["storage", "Storage income"],
  ["other", "Anything else"],
];

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</label>
      {children}
      {hint && <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

export default function InvoiceExportCard() {
  const [s, setS] = useState(null);
  const [loaded, setLoaded] = useState({});
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState("");
  const [newContact, setNewContact] = useState("");

  useEffect(() => {
    invoiceExportSettings()
      .then((d) => { setS(d); setLoaded(d); })
      .catch(() => toast.error("Couldn't load invoice settings"));
  }, []);

  if (!s) {
    return (
      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-6" data-testid="invoice-export-card">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading invoice export settings…
        </div>
      </section>
    );
  }

  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));
  const setCode = (k, v) => setS((prev) => ({ ...prev, account_codes: { ...prev.account_codes, [k]: v } }));
  const setSource = (k, v) => setS((prev) => ({ ...prev, source_contacts: { ...prev.source_contacts, [k]: v } }));

  const addMapping = () => {
    if (!newName.trim() || !newContact.trim()) {
      toast.error("Enter both names");
      return;
    }
    setS((prev) => ({
      ...prev,
      contact_map: { ...prev.contact_map, [newName.trim().toLowerCase()]: newContact.trim() },
    }));
    setNewName(""); setNewContact("");
  };

  const removeMapping = (key) =>
    setS((prev) => {
      const next = { ...prev.contact_map };
      delete next[key];
      return { ...prev, contact_map: next };
    });

  const save = async () => {
    setSaving(true);
    try {
      const saved = await saveInvoiceExportSettings({
        invoice_prefix: s.invoice_prefix,
        // Only send the counter if it was actually edited. Sending the value
        // this page loaded with would wind it back over any export that ran
        // while Settings sat open, reissuing invoice numbers.
        ...(Number(s.next_number) !== Number(loaded.next_number)
          ? { next_number: Number(s.next_number) }
          : {}),
        due_days: Number(s.due_days),
        xero_tax_type: s.xero_tax_type,
        myob_tax_code: s.myob_tax_code,
        account_codes: s.account_codes,
        contact_map: s.contact_map,
        source_contacts: s.source_contacts,
      });
      setS(saved);
      setLoaded(saved);
      toast.success("Invoice export settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-6" data-testid="invoice-export-card">
      <div className="flex items-center gap-2 mb-1">
        <FileSpreadsheet className="h-5 w-5 text-blue-600" />
        <h2 className="font-display text-lg font-black text-slate-800 dark:text-slate-100">Invoice Export</h2>
      </div>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
        How your jobs translate into Xero or MYOB. These have to match your accounting
        system exactly — a near-miss on a name creates a duplicate customer, and a blank
        tax rate imports the invoice with no GST on it.
      </p>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Invoice number prefix" hint="Kept short — MYOB allows 8 characters in total.">
          <Input value={s.invoice_prefix || ""} onChange={(e) => set("invoice_prefix", e.target.value)}
                 className="mt-1" data-testid="ie-prefix" />
        </Field>
        <Field label="Next invoice number" hint="Counts up on its own with every export.">
          <Input type="number" value={s.next_number ?? ""} onChange={(e) => set("next_number", e.target.value)}
                 className="mt-1" data-testid="ie-next-number" />
        </Field>
        <Field label="Payment terms (days)" hint="Due date = job date plus this many days.">
          <Input type="number" value={s.due_days ?? ""} onChange={(e) => set("due_days", e.target.value)}
                 className="mt-1" data-testid="ie-due-days" />
        </Field>
        <Field label="Xero tax rate name" hint='Exactly as Xero shows it, e.g. "15% GST on Income".'>
          <Input value={s.xero_tax_type || ""} onChange={(e) => set("xero_tax_type", e.target.value)}
                 className="mt-1" placeholder="15% GST on Income" data-testid="ie-xero-tax" />
        </Field>
        <Field label="MYOB tax code" hint='Usually "GST". AccountRight only.'>
          <Input value={s.myob_tax_code || ""} onChange={(e) => set("myob_tax_code", e.target.value)}
                 className="mt-1" placeholder="GST" data-testid="ie-myob-tax" />
        </Field>
      </div>

      <div className="mt-5">
        <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">
          Account codes (from your chart of accounts)
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {CODE_FIELDS.map(([key, label]) => (
            <Field key={key} label={label}>
              <Input
                value={s.account_codes?.[key] || ""}
                onChange={(e) => setCode(key, e.target.value)}
                placeholder="e.g. 200"
                className="mt-1"
                data-testid={`ie-code-${key}`}
              />
            </Field>
          ))}
        </div>
      </div>

      <div className="mt-5">
        <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1">
          Who partner work is billed to
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
          AA and First Rescue jobs get invoiced to them, not to the motorist whose car
          they sent you to. Leave blank to bill the name on the job.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="AA jobs">
            <Input value={s.source_contacts?.aa || ""} onChange={(e) => setSource("aa", e.target.value)}
                   placeholder="Their name in Xero/MYOB" className="mt-1" data-testid="ie-contact-aa" />
          </Field>
          <Field label="First Rescue jobs">
            <Input value={s.source_contacts?.first_rescue || ""} onChange={(e) => setSource("first_rescue", e.target.value)}
                   placeholder="Their name in Xero/MYOB" className="mt-1" data-testid="ie-contact-fr" />
          </Field>
        </div>
      </div>

      <div className="mt-5">
        <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1">
          Name corrections
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
          For regulars whose name you hold differently from your accounting system.
        </p>
        <div className="space-y-2" data-testid="ie-contact-map">
          {Object.entries(s.contact_map || {}).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 text-sm" data-testid={`ie-map-row-${k}`}>
              <span className="flex-1 truncate text-slate-600 dark:text-slate-300">{k}</span>
              <span className="text-slate-400">→</span>
              <span className="flex-1 truncate font-semibold text-slate-800 dark:text-slate-100">{v}</span>
              <Button type="button" variant="ghost" size="sm" onClick={() => removeMapping(k)} data-testid={`ie-map-remove-${k}`}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                   placeholder="Name in TowHook" data-testid="ie-map-new-name" />
            <Input value={newContact} onChange={(e) => setNewContact(e.target.value)}
                   placeholder="Name in Xero / MYOB" data-testid="ie-map-new-contact" />
            <Button type="button" variant="outline" onClick={addMapping} data-testid="ie-map-add">
              <Plus className="h-4 w-4" /> Add
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <Button type="button" onClick={save} disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="ie-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save
        </Button>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          Amounts always export excluding GST — pick “Tax Exclusive” on import.
        </span>
      </div>
    </section>
  );
}
